import React, { FC } from 'react';
import CollapsedSection from '@components/CollapsedSection';
import PopoverWithArrow from '@components/PopoverWithArrow/PopoverWithArrow';
import { DomainItem } from '@constants/forms/scope-settings';
import useTrancatedWithCount from '@hooks/useTrancatedWithCount';
import { Box, Stack, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import { formatDomainsToStrings } from '@utils/helpers';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';

type VirtualDomainsAccordionProps = {
  title: string;
  domains: DomainItem[];
};

const VirtualDomainsAccordion: FC<VirtualDomainsAccordionProps> = ({
  title,
  domains,
}) => {
  const formattedDomainsStrings = formatDomainsToStrings(domains);
  const { visibleText, hiddenCount, containerRef } = useTrancatedWithCount(
    formattedDomainsStrings,
  );

  const popupState = usePopupState({
    variant: 'popover',
    popupId: `domains-popup`,
  });

  // Show empty state message when no domains exist
  if (!domains.length) {
    return (
      <Stack gap={1}>
        <CollapsedSection
          defaultExpanded
          title={title}
          sx={{ p: 0 }}
          unmountOnExit={false}
        >
          <Typography color="text.secondary">
            No underpinning Operational Control
          </Typography>
        </CollapsedSection>
      </Stack>
    );
  }

  return (
    <Stack gap={1}>
      <CollapsedSection
        defaultExpanded
        title={title}
        sx={{ p: 0 }}
        unmountOnExit={false}
      >
        <Typography
          ref={containerRef}
          {...(hiddenCount > 0 ? bindTrigger(popupState) : {})}
        >
          {visibleText}
          {hiddenCount > 0 && (
            <>
              {'... '}
              <span style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                +{hiddenCount} more
              </span>
            </>
          )}
        </Typography>
      </CollapsedSection>

      <PopoverWithArrow
        {...bindPopover(popupState)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: { sx: { width: 365, mt: 4, p: 0 } },
        }}
      >
        <Box p={4} sx={{ wordBreak: 'break-word' }}>
          {formattedDomainsStrings.map((string, index) => (
            <Box key={string}>
              <Typography sx={{ display: 'inline-flex', mr: 1 }}>
                {string}
              </Typography>

              {index < formattedDomainsStrings.length - 1 && (
                <Divider sx={{ mt: 1.5, mb: 1 }} />
              )}
            </Box>
          ))}
        </Box>
      </PopoverWithArrow>
    </Stack>
  );
};

export default VirtualDomainsAccordion;
